import { WAITLIST_PATH } from '@/lib/landing'
import { card, pad } from './primitives'

const FAQ = [
  {
    q: 'Is it free?',
    a: 'Yes, and it stays free. Everything you do in the app is free, and you can make images, video and sound without ever paying (local 3D is experimental on Mac): local models run on your own Mac. They just take a little patience, or a powerful machine. You pay only if you choose cloud models, with Genie credits or your own fal.ai or Replicate key.',
  },
  {
    q: 'Do I need an account?',
    a: 'Yes. Genie asks you to sign in on first launch, with Google, GitHub, X or an email link. The same account works at app.genie.art and holds your credits.',
  },
  {
    q: 'What leaves my Mac?',
    a: 'Local models run on your GPU. Your outputs in ~/.genie/outputs sync to your Genie account so they follow you. Prompts go to a cloud provider only when you pick a cloud model, and the agent sends its requests to the model you connect, or nowhere if you run it on Ollama.',
  },
  {
    q: 'Which models run locally?',
    a: 'Z-Image Turbo, FLUX.2 klein, SDXL and SD 1.5 for images; Wan 2.2 for video; ACE-Step for music; TRELLIS.2 for 3D (experimental on Mac); plus SeedVR2, BiRefNet and LaMa for upscaling, cutouts and erasing. The setup wizard checks your chip, memory and free disk, and preselects what fits while leaving 20 GB free.',
  },
  {
    q: 'Do I need anything installed first?',
    a: 'No. Genie downloads Python, git and ComfyUI into ~/.genie on first launch, shows progress, and retries a download that fails. To use the agent, connect a model in Settings → AI models: Claude, Grok, OpenAI, OpenRouter, or Ollama on your Mac.',
  },
  {
    q: 'macOS says Apple could not verify the app.',
    a: 'Beta builds are not notarized yet. Allow it once in System Settings → Privacy & Security. After that, Genie offers each update and installs it in one click.',
  },
  {
    q: 'Windows or Intel Mac?',
    a: 'The desktop app needs Apple Silicon: local generation depends on its unified-memory GPU. Join the waitlist and we will tell you when other platforms ship.',
    link: { label: 'Join the Windows waitlist', href: WAITLIST_PATH },
  },
  {
    q: 'Does it work with ComfyUI?',
    a: 'ComfyUI is Genie’s local engine: it is installed on first launch and runs every local model. With the official Comfy MCP added (a separate install), the agent can also build and run simple ComfyUI image workflows itself.',
  },
] as const

export function Faq() {
  return (
    <section id="faq" className="landing-shell scroll-mt-20 pt-28" aria-labelledby="faq-heading">
      <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)]">
        <div>
          <span className="eyebrow">Questions</span>
          <h2 id="faq-heading" className="mt-4 max-w-sm text-[clamp(28px,3.4vw,36px)] leading-[1.1] tracking-[-0.02em]">
            Before you download
          </h2>
          <p className="mt-4 max-w-sm text-[14px] leading-relaxed text-ink-2">
            Anything else goes to{' '}
            <a href="mailto:hey@genie.art" className="text-ink underline decoration-rule-strong underline-offset-4 hover:decoration-ink">
              hey@genie.art
            </a>
            .
          </p>
        </div>
        <dl className={card}>
          {FAQ.map((item, i) => (
            <details key={item.q} className="group border-t border-dashed border-rule first:border-t-0">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-6 px-5 py-4 text-left marker:content-none [&::-webkit-details-marker]:hidden">
                <dt className="flex items-baseline gap-4">
                  <span className="num text-[12px] text-ink-3">{pad(i + 1)}</span>
                  <span className="text-[16px] leading-snug tracking-[-0.01em] text-ink">{item.q}</span>
                </dt>
                <span aria-hidden className="font-mono text-[16px] leading-none text-ink-3 transition-transform group-open:rotate-45">
                  +
                </span>
              </summary>
              <dd className="max-w-[62ch] px-5 pb-5 pl-[52px] text-[14px] leading-relaxed text-ink-2">
                {item.a}
                {'link' in item && (
                  <>
                    {' '}
                    <a href={item.link.href} className="text-ink underline decoration-rule-strong underline-offset-4 hover:decoration-ink">
                      {item.link.label}
                    </a>
                  </>
                )}
              </dd>
            </details>
          ))}
        </dl>
      </div>
    </section>
  )
}
